/**
 * For fixed Shopify bundles (0 options / 1 parent variant), Liquid cannot
 * read the selected component variants. A prefetch starts as soon as the
 * picker HTML is parsed; this module paints the real sizes/prices and
 * removes the skeleton. Missing price/variant data clears the skeleton.
 */
const QUERY = `
  query BundleComponents($id: ID!) {
    node(id: $id) {
      ... on ProductVariant {
        components(first: 30) {
          nodes {
            productVariant {
              id
              title
              price { amount }
              compareAtPrice { amount }
              selectedOptions { name value }
              product { id title }
            }
          }
        }
      }
    }
  }
`;

function parseGid(gid) {
  if (!gid) return '';
  const parts = String(gid).split('/');
  return parts[parts.length - 1] || '';
}

function amountToCents(amount) {
  const n = Number.parseFloat(amount);
  if (Number.isNaN(n)) return 0;
  return Math.round(n * 100);
}

function formatMoney(cents, format) {
  // Always whole rupees — bundle % split can leave paise on sale price
  const whole = Math.round(Number(cents) / 100).toString();
  const withCommas = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  if (!format) return `₹ ${withCommas}`;
  return format
    .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, withCommas)
    .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, withCommas)
    .replace(/\{\{\s*amount\s*\}\}/g, withCommas);
}

function variantLabel(componentVariant) {
  const options = componentVariant.selectedOptions || [];
  const meaningful = options
    .map((option) => option.value)
    .filter((value) => value && value !== 'Default Title');
  if (meaningful.length) return meaningful[meaningful.length - 1];
  if (componentVariant.title && componentVariant.title !== 'Default Title') {
    return componentVariant.title;
  }
  return componentVariant.title || '';
}

async function fetchComponents(variantId) {
  if (window.__bundleFixedPrefetch) {
    try {
      const prefetched = await window.__bundleFixedPrefetch;
      if (Array.isArray(prefetched) && prefetched.length) return prefetched;
    } catch {
      // fall through to a fresh request
    }
  }

  const endpoints = [
    `${window.Shopify?.routes?.root || '/'}api/2026-07/graphql.json`,
    `https://${window.Shopify?.shop}/api/2026-07/graphql.json`,
  ];
  const body = JSON.stringify({
    query: QUERY,
    variables: { id: `gid://shopify/ProductVariant/${variantId}` },
  });
  const headers = { 'Content-Type': 'application/json' };
  const token =
    document.querySelector('meta[name="shopify-storefront-access-token"]')?.content ||
    window.Shopify?.storefrontAccessToken;
  if (token) headers['X-Shopify-Storefront-Access-Token'] = token;

  for (const endpoint of endpoints) {
    try {
      const response = await fetch(endpoint, {
        method: 'POST',
        headers,
        body,
      });
      if (!response.ok) continue;
      const json = await response.json();
      const nodes = json?.data?.node?.components?.nodes;
      if (Array.isArray(nodes) && nodes.length) return nodes;
    } catch {
      // try next endpoint
    }
  }
  return [];
}

function revealPriceBadge() {
  document.querySelectorAll('.price-display--bundle-pending').forEach((el) => {
    el.classList.remove('price-display--bundle-pending');
    const badge = el.querySelector('.price-discount-mrp');
    if (badge) badge.hidden = false;
  });
}

function clearPrices(fieldset) {
  const pricesEl = fieldset.querySelector('.variant-option__bundle-prices');
  if (pricesEl) pricesEl.replaceChildren();
}

function markVariantReady(fieldset, input) {
  fieldset.classList.remove('variant-option--bundle-skeleton');
  const label = fieldset.querySelector('.variant-option__button-label--bundle-loading');
  if (label) label.classList.remove('variant-option__button-label--bundle-loading');
  if (input) {
    input.checked = true;
    input.dataset.currentChecked = 'true';
  }
}

function revealFieldset(fieldset, label) {
  const textEl = fieldset.querySelector('[data-key="variant-option-text"]');
  const input = fieldset.querySelector('input');
  const resolvedLabel = label || fieldset.getAttribute('data-fallback-label') || '';

  if (resolvedLabel) {
    if (textEl) textEl.textContent = resolvedLabel;
    if (input) {
      input.value = resolvedLabel;
      input.setAttribute('aria-label', resolvedLabel);
    }
  } else if (textEl) {
    textEl.textContent = '';
  }

  const compare = Number.parseInt(fieldset.getAttribute('data-component-compare') || '0', 10);
  if (compare <= 0) clearPrices(fieldset);

  markVariantReady(fieldset, input);
}

function revealFallback(picker) {
  picker.querySelectorAll('[data-bundle-product-id]').forEach((fieldset) => {
    revealFieldset(fieldset);
  });
  revealPriceBadge();
  picker.removeAttribute('data-bundle-pending');
}

function applyComponents(picker, nodes) {
  const moneyFormat = picker.dataset.moneyFormat || '';
  const salePrice = Number.parseInt(picker.dataset.bundleSalePrice || '0', 10);
  const fieldsets = picker.querySelectorAll('[data-bundle-product-id]');
  if (!fieldsets.length) return false;

  let regularTotal = 0;
  const byProductId = new Map();

  nodes.forEach((node, index) => {
    const componentVariant = node.productVariant;
    if (!componentVariant) return;
    const productId = parseGid(componentVariant.product?.id);
    const compare =
      amountToCents(componentVariant.compareAtPrice?.amount) || amountToCents(componentVariant.price?.amount);
    regularTotal += compare;
    const payload = { componentVariant, compare, index };
    if (productId) byProductId.set(productId, payload);
    byProductId.set(`index:${index}`, payload);
  });

  const discountAmount = Math.max(regularTotal - salePrice, 0);
  const remainingPercent =
    regularTotal > 0 && salePrice > 0
      ? (10000 - Math.round((discountAmount * 10000) / regularTotal)) / 100
      : 100;

  fieldsets.forEach((fieldset, index) => {
    const productId = fieldset.getAttribute('data-bundle-product-id');
    const match = byProductId.get(productId) || byProductId.get(`index:${index}`);

    if (!match) {
      revealFieldset(fieldset);
      return;
    }

    const label = variantLabel(match.componentVariant);
    const textEl = fieldset.querySelector('[data-key="variant-option-text"]');
    const input = fieldset.querySelector('input');
    if (textEl && label) textEl.textContent = label;
    if (input && label) {
      input.value = label;
      input.setAttribute('aria-label', label);
    }

    const itemCompare = match.compare;
    // Round to nearest rupee (100 paise) so sale never shows decimals
    const itemSale = Math.round(Math.round((itemCompare * remainingPercent) / 100) / 100) * 100;
    const saleEl = fieldset.querySelector('.variant-option__bundle-price--sale');
    const compareEl = fieldset.querySelector('.variant-option__bundle-price--compare');

    if (itemSale > 0) {
      if (saleEl) saleEl.textContent = formatMoney(itemSale, moneyFormat);
      if (compareEl) {
        compareEl.textContent = itemCompare > itemSale ? formatMoney(itemCompare, moneyFormat) : '';
      }
    } else {
      clearPrices(fieldset);
    }

    markVariantReady(fieldset, input);
  });

  const mrpValue = document.querySelector('.price-display--mrp .compare-at-price.price-display__mrp-value');
  const discountEl = document.querySelector('.price-display--mrp .price-discount-mrp');

  if (regularTotal > salePrice) {
    if (mrpValue) mrpValue.textContent = formatMoney(regularTotal, moneyFormat);
    if (discountEl) {
      discountEl.textContent = ` ${Math.round((discountAmount * 100) / regularTotal)}% OFF `;
      discountEl.hidden = false;
    }
  } else if (mrpValue && salePrice > 0) {
    mrpValue.textContent = formatMoney(salePrice, moneyFormat);
    if (discountEl) discountEl.hidden = true;
  }

  revealPriceBadge();
  picker.removeAttribute('data-bundle-pending');
  return true;
}

async function init() {
  const pickers = document.querySelectorAll('variant-picker.variant-picker--bundle[data-bundle-fixed="true"]');
  for (const picker of pickers) {
    const variantId = picker.dataset.variantId;
    if (!variantId) {
      revealFallback(picker);
      continue;
    }
    try {
      const nodes = await fetchComponents(variantId);
      if (!nodes.length || !applyComponents(picker, nodes)) {
        revealFallback(picker);
      }
    } catch {
      revealFallback(picker);
    }
  }

  // Mixed bundles: leftover fixed rows never use the API — clear any stray skeletons.
  document
    .querySelectorAll('variant-picker.variant-picker--bundle:not([data-bundle-fixed="true"]) .variant-option--bundle-skeleton')
    .forEach((fieldset) => {
      revealFieldset(fieldset);
    });
}

init();
